import { useFetcher } from 'react-router'
import { buildWhatsAppUrl } from '@/lib/siteConfig'
import { getEnrollment } from '@/lib/intake'
import { useCurrentTime } from '@/lib/hydration'
import { cn } from '@/lib/utils'
import { CONTACT_PATH } from '@/lib/portal/paths'
import type { EnquiryField, EnquiryResult } from '@/lib/portal/enquiries'
import { Alert } from '@/components/ui/Alert'
import { buttonClassName } from '@/components/ui/buttonStyles'
import { Input } from '@/components/ui/Input'
import { IconArrowRight, IconCheck, IconWhatsApp } from '@/components/Icons'

type EnrollmentFormProps = {
  courseSlug: string
  courseTitle: string
  isOpen: boolean
  batchSize: string
}

export function EnrollmentForm({
  courseSlug,
  courseTitle,
  isOpen,
  batchSize,
}: EnrollmentFormProps) {
  const enrollment = getEnrollment(useCurrentTime())
  const fetcher = useFetcher<{ result: EnquiryResult }>()
  const result = fetcher.data?.result
  const sending = fetcher.state !== 'idle'
  const fieldError = (field: EnquiryField) =>
    result && !result.ok && result.field === field ? result.error : undefined
  const whatsappOnlyUrl = buildWhatsAppUrl(
    `Hi Skillo Academy, I want counselling for ${courseTitle} (${enrollment.batchMonth} ${enrollment.batchYear} intake).`,
  )

  if (!isOpen) return null

  if (result?.ok) {
    return (
      <div
        id="apply"
        className="scroll-mt-32 rounded-2xl border border-green-400/30 bg-green-400/[0.06] p-6 text-center"
      >
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-400/10">
          <IconCheck size={24} aria-hidden="true" className="text-green-300" />
        </div>
        <h3 className="mb-1 font-display text-lg font-bold text-[var(--text-strong)]">
          Enquiry sent
        </h3>
        <p role="status" className="mb-4 text-sm text-[var(--surface-muted)]">
          {result.message}
          <br />
          <span className="font-bangla">
            আপনার এনকোয়ারি একাডেমি অফিসে পৌঁছেছে। শীঘ্রই হোয়াটসঅ্যাপে যোগাযোগ করা হবে।
          </span>
        </p>
        <a
          href={result.whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonClassName({ variant: 'accent', fullWidth: true, className: 'py-3.5' })}
        >
          <IconWhatsApp size={18} aria-hidden="true" />
          Continue on WhatsApp
        </a>
      </div>
    )
  }

  return (
    <fetcher.Form
      method="post"
      action={CONTACT_PATH}
      id="apply"
      noValidate
      className="scroll-mt-32 rounded-2xl border border-[var(--surface-border)] bg-[var(--surface-card)] p-6"
    >
      <h3 className="mb-1 font-display text-lg font-bold text-[var(--text-strong)]">
        Request counselling | কাউন্সেলিং চান
      </h3>
      <p className="mb-5 text-sm text-[var(--surface-muted)]">
        Share your name and WhatsApp number. The office will confirm fit, schedule, and payment
        before any seat is booked.
      </p>

      <input type="hidden" name="source" value="course_page" />
      <input type="hidden" name="courseSlug" value={courseSlug} />
      <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label>
          Website
          <input type="text" name="website" tabIndex={-1} autoComplete="off" />
        </label>
      </div>

      <div className="mb-5 flex flex-col gap-3">
        {result && !result.ok && !result.field && <Alert tone="error">{result.error}</Alert>}
        <Input
          label="Your name | আপনার নাম"
          name="name"
          autoComplete="name"
          required
          placeholder="e.g. Rahim Ahmed"
          error={fieldError('name')}
        />
        <Input
          label="WhatsApp number | হোয়াটসঅ্যাপ নম্বর"
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          required
          placeholder="e.g. 017XXXXXXXX"
          error={fieldError('phone')}
        />
      </div>

      <button
        type="submit"
        disabled={sending}
        className={cn(
          buttonClassName({ variant: 'primary', fullWidth: true }),
          'group rounded-full py-3.5 leading-snug',
        )}
      >
        {sending ? 'Sending…' : 'Send enquiry | এনকোয়ারি পাঠান'}
        <IconArrowRight
          size={16}
          aria-hidden="true"
          className="transition-transform group-hover:translate-x-0.5"
        />
      </button>

      <a
        href={whatsappOnlyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 flex items-center justify-center gap-2 text-sm font-semibold text-green-300 no-underline hover:text-green-200"
      >
        <IconWhatsApp size={16} aria-hidden="true" />
        Or message the office on WhatsApp
      </a>

      <p className="mt-3 text-center text-xs text-[var(--surface-muted)]">
        Next batch: {enrollment.nextBatchDate} · {batchSize}
      </p>
    </fetcher.Form>
  )
}
